import {
  ArrowLeft,
  Download,
  FileText,
  Image as ImageIcon,
  LoaderCircle,
  MessageCircleMore,
  RefreshCw,
  Search,
  UsersRound,
  Video,
  Volume2
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent
} from "react";
import {
  OpsEvidenceAnchorSchema,
  opsSha256Utf8,
  type OpsEvidenceAnchor
} from "@business-ops/domain";
import {
  api,
  type WhatsAppBrowserAttachment,
  type WhatsAppBrowserConversation,
  type WhatsAppBrowserMessage
} from "../api.js";
import { SafeLinkedText } from "../components/safe-linked-text.js";
import {
  maskPersonalPhoneNumbers,
  projectPrivateTextSpan,
  usePrivateModePhoneMasking
} from "../privacy-mode.js";

type ConversationKind = WhatsAppBrowserConversation["kind"];
type DirectoryLoadMode = "REPLACE" | "APPEND" | "POLL";
type ThreadLoadMode = "REPLACE" | "PREPEND" | "POLL";

export function readOpsEvidenceAnchor(state: unknown, sourceEventId: string | null): OpsEvidenceAnchor | null {
  if (!sourceEventId || !state || typeof state !== "object" || Array.isArray(state)) return null;
  const parsed = OpsEvidenceAnchorSchema.safeParse(
    (state as Record<string, unknown>).businessOpsEvidenceAnchor
  );
  return parsed.success && parsed.data.sourceEventId === sourceEventId ? parsed.data : null;
}

function normalizedOpsEvidenceSource(value: string): string {
  return value.normalize("NFKC")
    .replace(/[\u0000-\u001f\u007f]+/gu, " ")
    .replace(/\s+/gu, " ")
    .trim();
}

export function resolveOpsEvidenceAnchor(
  content: string,
  anchor: OpsEvidenceAnchor
): { excerpt: string; highlightStart: number; highlightEnd: number } | null {
  // WHY [BOL-OPS-SOURCE-SPAN-ANCHOR-001]: WhatsApp source excerpts use a
  // normalized/bounded public projection, while Conversations retains the
  // complete captured message. Recreate only known projections and require the
  // exact digest before marking; otherwise focus the message without a false
  // highlight. Falsifier: changed bytes receive the old mark.
  const normalized = normalizedOpsEvidenceSource(content);
  const boundedNormalized = normalized.length <= anchor.excerptLength
    ? normalized
    : `${normalized.slice(0, anchor.excerptLength - 1).trimEnd()}…`;
  const prefixNormalized = normalized.slice(0, anchor.excerptLength);
  for (const excerpt of new Set([content, normalized, boundedNormalized, prefixNormalized])) {
    if (excerpt.length !== anchor.excerptLength) continue;
    if (opsSha256Utf8(excerpt) !== anchor.excerptHash) continue;
    return {
      excerpt,
      highlightStart: anchor.highlightStart,
      highlightEnd: anchor.highlightEnd
    };
  }
  return null;
}

function HighlightedSourceText({
  excerpt,
  highlightStart,
  highlightEnd,
  maskPhones
}: {
  excerpt: string;
  highlightStart: number;
  highlightEnd: number;
  maskPhones: boolean;
}) {
  const projected = projectPrivateTextSpan(excerpt, highlightStart, highlightEnd, maskPhones);
  if (!projected) return null;
  return <>
    <SafeLinkedText text={projected.before} />
    <mark data-ops-evidence-highlight="true"><SafeLinkedText text={projected.span} /></mark>
    <SafeLinkedText text={projected.after} />
  </>;
}

/**
 * Read-only evidence for the connected WhatsApp account.
 *
 * WHY [BOL-WA-CONVERSATION-UI-001]: search snippets and the Settings rule
 * roster cannot let the operator inspect a complete chat, its replies, or its files.
 * Keep this surface read-only and keyed only by internal UUIDs: opening or
 * refreshing it must never send, mark read, mutate policy, or call Luna.
 * Falsifier: a group/DM needs the phone to verify its source, a raw JID/storage
 * key appears in the browser, or browsing changes any WhatsApp/Work record.
 */
export function ConversationsView({
  focusSourceEventId,
  focusSource,
  clearFocusSource,
  returnToOpsChat,
  openWork,
  openApprovals
}: {
  focusSourceEventId: string | null;
  focusSource: (sourceEventId: string) => void;
  clearFocusSource: () => void;
  returnToOpsChat?: (() => void) | undefined;
  openWork: (workItemId: string) => void;
  openApprovals: () => void;
}) {
  const maskPhones = usePrivateModePhoneMasking();
  const [queryInput, setQueryInput] = useState("");
  const [query, setQuery] = useState("");
  const [kind, setKind] = useState<ConversationKind | "">("");
  const [conversations, setConversations] = useState<WhatsAppBrowserConversation[]>([]);
  const [directoryCursor, setDirectoryCursor] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [directoryBusy, setDirectoryBusy] = useState(true);
  const [directoryMoreBusy, setDirectoryMoreBusy] = useState(false);
  const [directoryError, setDirectoryError] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [mobileThreadOpen, setMobileThreadOpen] = useState(false);
  const [messages, setMessages] = useState<WhatsAppBrowserMessage[]>([]);
  const [threadConversation, setThreadConversation] = useState<WhatsAppBrowserConversation | null>(null);
  const [threadCursor, setThreadCursor] = useState<string | null>(null);
  const [threadBusy, setThreadBusy] = useState(false);
  const [threadEarlierBusy, setThreadEarlierBusy] = useState(false);
  const [threadError, setThreadError] = useState("");
  const directorySequence = useRef(0);
  const threadSequence = useRef(0);
  const messagePane = useRef<HTMLDivElement | null>(null);
  const selectedIdRef = useRef<string | null>(selectedId);
  const focusSourceRef = useRef<string | null>(focusSourceEventId);
  focusSourceRef.current = focusSourceEventId;
  const evidenceAnchor = readOpsEvidenceAnchor(
    typeof window === "undefined" ? null : window.history.state,
    focusSourceEventId
  );

  const loadDirectory = useCallback(async (input: {
    query: string;
    kind: ConversationKind | "";
    cursor?: string;
    mode: DirectoryLoadMode;
  }) => {
    const sequence = ++directorySequence.current;
    if (input.mode === "APPEND") setDirectoryMoreBusy(true);
    else if (input.mode === "REPLACE") setDirectoryBusy(true);
    if (input.mode !== "POLL") setDirectoryError("");
    try {
      const request: {
        query?: string;
        kind?: ConversationKind;
        cursor?: string;
        limit: number;
      } = { limit: 30 };
      if (input.query) request.query = input.query;
      if (input.kind) request.kind = input.kind;
      if (input.cursor) request.cursor = input.cursor;
      const page = await api.whatsAppBrowserConversations(request);
      if (sequence !== directorySequence.current) return;
      setConversations((current) => {
        const next = input.mode === "APPEND"
          ? mergeConversationDirectory(current, page.conversations, false)
          : input.mode === "POLL"
            ? mergeConversationDirectory(current, page.conversations, true)
            : page.conversations;
        setSelectedId((selected) => {
          if (focusSourceRef.current) return selected;
          return selected && next.some((entry) => entry.id === selected)
            ? selected
            : next[0]?.id ?? null;
        });
        return next;
      });
      setDirectoryCursor(page.nextCursor);
      setTotalCount((current) => page.totalCount > 0 || input.mode === "REPLACE" ? page.totalCount : current);
    } catch (error) {
      if (sequence !== directorySequence.current) return;
      setDirectoryError(readableError(error, "Conversations could not be loaded."));
    } finally {
      if (sequence === directorySequence.current) {
        setDirectoryBusy(false);
        setDirectoryMoreBusy(false);
      }
    }
  }, []);

  const loadThread = useCallback(async (conversationId: string, input: {
    cursor?: string;
    mode: ThreadLoadMode;
  }) => {
    const sequence = ++threadSequence.current;
    const pane = messagePane.current;
    const priorHeight = pane?.scrollHeight ?? 0;
    const stayAtBottom = input.mode === "REPLACE" || Boolean(
      pane && pane.scrollTop + pane.clientHeight >= pane.scrollHeight - 100
    );
    if (input.mode === "PREPEND") setThreadEarlierBusy(true);
    else if (input.mode === "REPLACE") setThreadBusy(true);
    if (input.mode !== "POLL") setThreadError("");
    try {
      const request: { cursor?: string; limit: number } = { limit: 60 };
      if (input.cursor) request.cursor = input.cursor;
      const page = await api.whatsAppBrowserThread(conversationId, request);
      if (sequence !== threadSequence.current || selectedIdRef.current !== conversationId) return;
      setThreadConversation(page.conversation);
      setConversations((current) => current.map((entry) => entry.id === page.conversation.id ? page.conversation : entry));
      setMessages((current) => input.mode === "REPLACE"
        ? page.messages
        : input.mode === "PREPEND"
          ? mergeWhatsAppBrowserMessages(page.messages, current)
          : mergeWhatsAppBrowserMessages(current, page.messages));
      if (input.mode !== "POLL") setThreadCursor(page.nextCursor);
      requestAnimationFrame(() => requestAnimationFrame(() => {
        const currentPane = messagePane.current;
        if (!currentPane) return;
        if (input.mode === "PREPEND") currentPane.scrollTop += currentPane.scrollHeight - priorHeight;
        else if (stayAtBottom) currentPane.scrollTop = currentPane.scrollHeight;
      }));
    } catch (error) {
      if (sequence !== threadSequence.current) return;
      setThreadError(readableError(error, "This conversation could not be loaded."));
    } finally {
      if (sequence === threadSequence.current) {
        setThreadBusy(false);
        setThreadEarlierBusy(false);
      }
    }
  }, []);

  useEffect(() => { selectedIdRef.current = selectedId; }, [selectedId]);

  const loadFocusedContext = useCallback(async (sourceEventId: string) => {
    const sequence = ++threadSequence.current;
    setThreadBusy(true);
    setThreadError("");
    try {
      const page = await api.whatsAppBrowserMessageContext(sourceEventId);
      if (sequence !== threadSequence.current || focusSourceRef.current !== sourceEventId) return;
      selectedIdRef.current = page.conversation.id;
      setSelectedId(page.conversation.id);
      setMobileThreadOpen(true);
      setThreadConversation(page.conversation);
      setConversations((current) => mergeConversationDirectory(current, [page.conversation], true));
      setMessages(page.messages);
      setThreadCursor(page.nextCursor);
      requestAnimationFrame(() => requestAnimationFrame(() => {
        const target = document.getElementById(`wa-message-${sourceEventId}`);
        if (!(target instanceof HTMLElement)) return;
        target.scrollIntoView({ block: "center" });
        target.focus({ preventScroll: true });
      }));
    } catch (error) {
      if (sequence !== threadSequence.current) return;
      setThreadError(readableError(error, "That cited WhatsApp message could not be loaded."));
    } finally {
      if (sequence === threadSequence.current) setThreadBusy(false);
    }
  }, []);

  useEffect(() => {
    void loadDirectory({ query, kind, mode: "REPLACE" });
  }, [kind, loadDirectory, query]);

  useEffect(() => {
    if (focusSourceEventId) {
      void loadFocusedContext(focusSourceEventId);
    }
  }, [focusSourceEventId, loadFocusedContext]);

  useEffect(() => {
    if (focusSourceEventId) return;
    if (!selectedId) {
      setMessages([]);
      setThreadConversation(null);
      setThreadCursor(null);
      return;
    }
    void loadThread(selectedId, { mode: "REPLACE" });
  }, [focusSourceEventId, loadThread, selectedId]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      void loadDirectory({ query, kind, mode: "POLL" });
    }, 30_000);
    return () => window.clearInterval(interval);
  }, [kind, loadDirectory, query]);

  useEffect(() => {
    if (!selectedId || focusSourceEventId) return;
    const interval = window.setInterval(() => {
      void loadThread(selectedId, { mode: "POLL" });
    }, 15_000);
    return () => window.clearInterval(interval);
  }, [focusSourceEventId, loadThread, selectedId]);

  const selected = useMemo(
    () => threadConversation?.id === selectedId
      ? threadConversation
      : conversations.find((entry) => entry.id === selectedId) ?? null,
    [conversations, selectedId, threadConversation]
  );

  const applySearch = (event: FormEvent) => {
    event.preventDefault();
    const normalized = queryInput.trim();
    if (normalized === query) {
      void loadDirectory({ query, kind, mode: "REPLACE" });
    } else {
      setQuery(normalized);
    }
  };

  const openConversation = (conversationId: string) => {
    clearFocusSource();
    setSelectedId(conversationId);
    setMobileThreadOpen(true);
  };

  return (
    <section className="conversation-browser" data-thread-open={mobileThreadOpen ? "true" : "false"}>
      <header className="conversation-browser__header">
        <div>
          <p className="eyebrow">Read-only source</p>
          <h1>Conversations</h1>
          <p>Everything captured from the connected WhatsApp.</p>
        </div>
        <div className="conversation-browser__header-actions">
          {returnToOpsChat ? <button className="conversation-browser__return" type="button" onClick={returnToOpsChat}><ArrowLeft size={17} aria-hidden="true" />Back to chat</button> : null}
          <div className="conversation-browser__count" aria-live="polite">
            <strong>{totalCount}</strong><span>conversations</span>
          </div>
        </div>
      </header>

      <div className="conversation-browser__body">
        <aside className="conversation-directory" aria-label="WhatsApp conversations">
          <form className="conversation-directory__tools" onSubmit={applySearch}>
            <label className="conversation-directory__search">
              <span className="sr-only">Search conversations or people</span>
              <Search size={17} aria-hidden="true" />
              <input value={queryInput} onChange={(event) => setQueryInput(event.target.value)} placeholder="Search chats or people" />
              <button type="submit" aria-label="Search">Go</button>
            </label>
            <label className="conversation-directory__filter">
              <span className="sr-only">Conversation type</span>
              <select value={kind} onChange={(event) => setKind(event.target.value as ConversationKind | "")}>
                <option value="">All chats</option>
                <option value="GROUP">Groups</option>
                <option value="DIRECT">Direct</option>
                <option value="BROADCAST">Broadcasts</option>
              </select>
            </label>
            <button className="conversation-directory__refresh" type="button" onClick={() => void loadDirectory({ query, kind, mode: "REPLACE" })} disabled={directoryBusy} aria-label="Refresh conversations">
              <RefreshCw className={directoryBusy ? "spin" : ""} size={17} aria-hidden="true" />
            </button>
          </form>

          {directoryError ? <p className="conversation-browser__error" role="alert">{directoryError}</p> : null}
          <div className="conversation-directory__list">
            {directoryBusy && conversations.length === 0 ? <LoadingState label="Loading conversations" /> : null}
            {!directoryBusy && conversations.length === 0 ? <EmptyState label="No captured conversations match this view." /> : null}
            {conversations.map((conversation) => (
              <button
                className={selectedId === conversation.id ? "conversation-row is-active" : "conversation-row"}
                type="button"
                key={conversation.id}
                onClick={() => openConversation(conversation.id)}
                aria-current={selectedId === conversation.id ? "true" : undefined}
              >
                <span className="conversation-row__icon" aria-hidden="true">
                  {conversation.kind === "GROUP" ? <UsersRound size={17} /> : <MessageCircleMore size={17} />}
                </span>
                <span className="conversation-row__content">
                  <span className="conversation-row__title"><strong>{conversation.label}</strong><time dateTime={conversation.lastMessageAt ?? undefined}>{shortConversationTime(conversation.lastMessageAt)}</time></span>
                  <span className="conversation-row__association">{conversationAssociationLabel(conversation)}</span>
                  <span className="conversation-row__preview">{conversation.latestMessage ? `${conversation.latestMessage.senderLabel}: ${conversation.latestMessage.preview}` : "No captured messages yet"}</span>
                </span>
              </button>
            ))}
            {directoryCursor ? <button className="conversation-directory__more" type="button" onClick={() => void loadDirectory({ query, kind, cursor: directoryCursor, mode: "APPEND" })} disabled={directoryMoreBusy}>
              {directoryMoreBusy ? <><LoaderCircle className="spin" size={16} />Loading</> : "Load more"}
            </button> : null}
          </div>
        </aside>

        <section className="conversation-thread" aria-label={selected ? `Messages in ${selected.label}` : "Conversation messages"}>
          {!selected && threadError ? <p className="conversation-browser__error" role="alert">{threadError}</p> : null}
          {selected ? <>
            <header className="conversation-thread__header">
              {/* WHY [BOL-OPS-SOURCE-RETURN-001]: a cited message is a child of
                  its originating Chat turn, while an ordinary thread is a child
                  of the directory. Keep the phone's default Back owner aligned
                  with that origin instead of dropping cited-message users into
                  an unrelated list. */}
              <button className="conversation-thread__back" type="button" onClick={returnToOpsChat ?? (() => setMobileThreadOpen(false))} aria-label={returnToOpsChat ? "Back to chat" : "Back to conversations"}><ArrowLeft size={19} /></button>
              <div>
                <h2>{selected.label}</h2>
                <p>{focusSourceEventId ? "Viewing cited message · " : ""}{conversationAssociationLabel(selected)} · {selected.messageCount.toLocaleString()} messages</p>
              </div>
              <span className="conversation-thread__actions">
                {focusSourceEventId ? <button className="conversation-thread__latest" type="button" onClick={clearFocusSource}>Latest</button> : null}
                <button className="conversation-thread__refresh" type="button" onClick={() => void (focusSourceEventId ? loadFocusedContext(focusSourceEventId) : loadThread(selected.id, { mode: "REPLACE" }))} disabled={threadBusy} aria-label={focusSourceEventId ? "Refresh cited message" : "Refresh this conversation"}>
                  <RefreshCw className={threadBusy ? "spin" : ""} size={17} aria-hidden="true" />
                </button>
              </span>
            </header>
            {threadError ? <p className="conversation-browser__error" role="alert">{threadError}</p> : null}
            <div className="conversation-thread__messages" ref={messagePane}>
              {threadCursor ? <button className="conversation-thread__earlier" type="button" onClick={() => void loadThread(selected.id, { cursor: threadCursor, mode: "PREPEND" })} disabled={threadEarlierBusy}>
                {threadEarlierBusy ? <><LoaderCircle className="spin" size={16} />Loading earlier messages</> : "Load earlier messages"}
              </button> : messages.length > 0 ? <p className="conversation-thread__start">Start of captured history</p> : null}
              {threadBusy && messages.length === 0 ? <LoadingState label="Loading messages" /> : null}
              {!threadBusy && messages.length === 0 ? <EmptyState label="No captured messages in this conversation." /> : null}
              {messages.map((message) => <MessageCard
                key={message.id}
                message={message}
                focused={message.id === focusSourceEventId}
                evidenceAnchor={message.id === focusSourceEventId ? evidenceAnchor : null}
                maskPhones={maskPhones}
                focusSource={focusSource}
                openWork={openWork}
                openApprovals={openApprovals}
              />)}
            </div>
          </> : <EmptyState label="Choose a conversation to read its captured history." />}
        </section>
      </div>
    </section>
  );
}

export function openWhatsAppReactionTarget(
  message: Pick<WhatsAppBrowserMessage, "reaction">,
  focusSource: (sourceEventId: string) => void
): boolean {
  if (message.reaction?.target.kind !== "AVAILABLE") return false;
  // WHY [BOL-WA-REACTION-TARGET-NAV-001]: a captured reaction may reference a
  // same-conversation message older than the mounted page. A DOM-only scroll
  // silently did nothing in that valid case. Route the exact internal source ID
  // through the existing focused-message owner so it loads canonical context
  // and preserves the originating Chat/proof Back receipt.
  focusSource(message.reaction.target.sourceEventId);
  return true;
}

export function MessageCard({
  message,
  focused,
  evidenceAnchor = null,
  maskPhones = true,
  focusSource,
  openWork,
  openApprovals
}: {
  message: WhatsAppBrowserMessage;
  focused: boolean;
  evidenceAnchor?: OpsEvidenceAnchor | null;
  maskPhones?: boolean;
  focusSource: (sourceEventId: string) => void;
  openWork: (workItemId: string) => void;
  openApprovals: () => void;
}) {
  const jumpToReply = () => {
    if (!message.replyTo?.sourceEventId) return;
    document.getElementById(`wa-message-${message.replyTo.sourceEventId}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
  };
  const jumpToReactionTarget = () => {
    openWhatsAppReactionTarget(message, focusSource);
  };
  const attachmentUrl = api.whatsAppBrowserAttachmentUrl(message.id);
  const inspectionDiffers = Boolean(
    message.attachment?.inspectionText
    && message.attachment.inspectionText.trim() !== message.content.trim()
  );
  const className = [
    "conversation-message",
    message.fromBusiness ? "is-business" : "",
    focused ? "is-focused" : ""
  ].filter(Boolean).join(" ");
  const exactAnchor = evidenceAnchor?.sourceEventId === message.id
    ? resolveOpsEvidenceAnchor(message.content, evidenceAnchor)
    : null;
  const inlineAnchor = exactAnchor?.excerpt === message.content ? exactAnchor : null;
  const privateContent = maskPhones ? maskPersonalPhoneNumbers(message.content) : message.content;
  return (
    <article id={`wa-message-${message.id}`} className={className} tabIndex={focused ? -1 : undefined}>
      <header><strong>{message.senderLabel}</strong><time dateTime={message.occurredAt}>{messageTime(message.occurredAt)}</time></header>
      {message.replyTo ? message.replyTo.sourceEventId ? (
        <button className="conversation-message__reply" type="button" onClick={jumpToReply}>
          <strong>{message.replyTo.senderLabel}</strong><span><SafeLinkedText text={message.replyTo.content} /></span>
        </button>
      ) : (
        <div className="conversation-message__reply is-unavailable"><strong>{message.replyTo.senderLabel}</strong><span><SafeLinkedText text={message.replyTo.content} /></span></div>
      ) : null}
      {message.reaction ? <div className="conversation-message__reaction">
        <small>Reaction</small>
        <strong>{message.reaction.text === null
          ? "Reaction text unavailable"
          : message.reaction.text === ""
            ? "Reaction removed"
            : <SafeLinkedText text={maskPhones
                ? maskPersonalPhoneNumbers(message.reaction.text) : message.reaction.text} />}</strong>
        {message.reaction.target.kind === "AVAILABLE" ? <button
          type="button"
          onClick={jumpToReactionTarget}
        >View reacted-to message</button> : <span>Reacted-to message unavailable.</span>}
      </div> : null}
      {message.kind === "VOICE_TRANSCRIPT" ? <small className="conversation-message__kind"><Volume2 size={13} />Voice transcript</small> : null}
      {exactAnchor && !inlineAnchor ? <div
        className="conversation-message__cited-excerpt"
        data-ops-evidence-anchor={message.id}
        data-ops-evidence-highlight-start={String(exactAnchor.highlightStart)}
        data-ops-evidence-highlight-end={String(exactAnchor.highlightEnd)}
      >
        <small>Cited excerpt</small>
        <p><HighlightedSourceText {...exactAnchor} maskPhones={maskPhones} /></p>
      </div> : null}
      {message.content ? <p
        className="conversation-message__content"
        {...(inlineAnchor ? {
          "data-ops-evidence-anchor": message.id,
          "data-ops-evidence-highlight-start": String(inlineAnchor.highlightStart),
          "data-ops-evidence-highlight-end": String(inlineAnchor.highlightEnd)
        } : {})}
      >{inlineAnchor
          ? <HighlightedSourceText {...inlineAnchor} maskPhones={maskPhones} />
          : <SafeLinkedText text={privateContent} />}</p> : null}
      {message.attachment ? <AttachmentView attachment={message.attachment} url={attachmentUrl} /> : null}
      {inspectionDiffers && message.attachment?.inspectionText ? <details className="conversation-message__inspection"><summary>Captured details</summary><p><SafeLinkedText text={message.attachment.inspectionText} /></p></details> : null}
      <footer>
        <span>{message.ingestionKind === "HISTORY" ? "Synced history" : "Captured live"}</span>
        {message.workOutcomes.length > 0 ? <span className="conversation-message__outcomes">
          {message.workOutcomes.map((outcome, index) => <WorkOutcomeAction
            key={outcome.proposalId ?? outcome.workItem?.id ?? `${message.id}:${index}`}
            outcome={outcome}
            openWork={openWork}
            openApprovals={openApprovals}
          />)}
        </span> : null}
      </footer>
    </article>
  );
}

function WorkOutcomeAction({
  outcome,
  openWork,
  openApprovals
}: {
  outcome: WhatsAppBrowserMessage["workOutcomes"][number];
  openWork: (workItemId: string) => void;
  openApprovals: () => void;
}) {
  const label = workOutcomeLabel(outcome.kind);
  if (outcome.kind === "READY_FOR_REVIEW") {
    return <button className="conversation-message__outcome" type="button" onClick={openApprovals}>{label}</button>;
  }
  if (outcome.workItem) {
    return <button className="conversation-message__outcome" type="button" onClick={() => openWork(outcome.workItem!.id)} aria-label={`${label}: ${outcome.workItem.title}`}>{label}</button>;
  }
  return <span className="conversation-message__outcome is-static">{label}</span>;
}

export function workOutcomeLabel(kind: WhatsAppBrowserMessage["workOutcomes"][number]["kind"]): string {
  if (kind === "READY_FOR_REVIEW") return "Task ready to review";
  if (kind === "PROCESSING") return "Creating task…";
  if (kind === "CREATED") return "Task created";
  if (kind === "UPDATED") return "Task updated";
  if (kind === "NOT_APPLIED") return "Task not applied";
  return "Task linked";
}

/**
 * WHY [BOL-WA-ATTACHMENT-OPEN-001]: retained WhatsApp media may reopen only
 * through the authenticated source-event endpoint, never a storage key, JID,
 * provider URL, or duplicated upload. Keep every available format directly
 * usable and every unavailable state explicit. Falsifier: an image is inert,
 * a player lacks a useful name, raw storage identity reaches markup, or a
 * missing/unsupported file still looks openable.
 */
export function AttachmentView({ attachment, url }: { attachment: WhatsAppBrowserAttachment; url: string }) {
  if (!attachment.available) {
    const status = attachmentStatusText(attachment);
    return <div className="conversation-attachment is-unavailable" role="status" aria-label={`${mediaLabel(attachment.mediaKind)} unavailable: ${status}`}><AttachmentIcon kind={attachment.mediaKind} /><span><strong>{attachment.fileName ?? mediaLabel(attachment.mediaKind)}</strong><small>{status}</small></span></div>;
  }
  const openLabel = attachmentActionLabel("Open", attachment);
  if (attachment.mediaKind === "IMAGE") {
    return <figure className="conversation-attachment conversation-attachment--image"><a href={url} target="_blank" rel="noopener noreferrer" aria-label={openLabel}><img src={url} alt={attachment.fileName ? `WhatsApp image: ${attachment.fileName}` : "WhatsApp image"} loading="lazy" /></a><figcaption>{attachment.fileName ?? "Image"}</figcaption></figure>;
  }
  if (attachment.mediaKind === "AUDIO") {
    return <div className="conversation-attachment conversation-attachment--audio"><Volume2 size={18} aria-hidden="true" /><audio aria-label={attachmentActionLabel("Play", attachment)} controls preload="metadata" src={url}>Your browser cannot play this audio.</audio></div>;
  }
  if (attachment.mediaKind === "VIDEO") {
    return <figure className="conversation-attachment conversation-attachment--video"><video aria-label={attachmentActionLabel("Play", attachment)} controls preload="metadata" src={url}>Your browser cannot play this video.</video><figcaption>{attachment.fileName ?? "Video"}</figcaption></figure>;
  }
  return <a className="conversation-attachment conversation-attachment--document" href={url} target="_blank" rel="noopener noreferrer" aria-label={openLabel}><FileText size={19} aria-hidden="true" /><span><strong>{attachment.fileName ?? "Document"}</strong><small>{formatBytes(attachment.byteCount)}</small></span><Download size={17} aria-hidden="true" /></a>;
}

function attachmentActionLabel(action: "Open" | "Play", attachment: WhatsAppBrowserAttachment): string {
  const kind = mediaLabel(attachment.mediaKind).toLocaleLowerCase();
  return `${action} WhatsApp ${kind}${attachment.fileName ? `: ${attachment.fileName}` : ""}`;
}

function AttachmentIcon({ kind }: { kind: WhatsAppBrowserAttachment["mediaKind"] }) {
  if (kind === "IMAGE") return <ImageIcon size={18} aria-hidden="true" />;
  if (kind === "AUDIO") return <Volume2 size={18} aria-hidden="true" />;
  if (kind === "VIDEO") return <Video size={18} aria-hidden="true" />;
  return <FileText size={18} aria-hidden="true" />;
}

function LoadingState({ label }: { label: string }) {
  return <div className="conversation-browser__state" role="status"><LoaderCircle className="spin" size={20} /><span>{label}</span></div>;
}

function EmptyState({ label }: { label: string }) {
  return <div className="conversation-browser__state"><MessageCircleMore size={22} /><span>{label}</span></div>;
}

export function mergeWhatsAppBrowserMessages(
  current: readonly WhatsAppBrowserMessage[],
  incoming: readonly WhatsAppBrowserMessage[]
): WhatsAppBrowserMessage[] {
  const byId = new Map<string, WhatsAppBrowserMessage>();
  for (const message of current) byId.set(message.id, message);
  for (const message of incoming) byId.set(message.id, message);
  return [...byId.values()].sort((left, right) => {
    const time = Date.parse(left.occurredAt) - Date.parse(right.occurredAt);
    return time || left.id.localeCompare(right.id);
  });
}

export function conversationAssociationLabel(conversation: WhatsAppBrowserConversation): string {
  if (conversation.client) return `Client: ${conversation.client.name}`;
  if (conversation.person) return `Person: ${conversation.person.name}`;
  if (conversation.kind === "GROUP") {
    return conversation.participantCount === 1 ? "1 participant" : `${conversation.participantCount} participants`;
  }
  return "Not linked";
}

export function attachmentStatusText(attachment: WhatsAppBrowserAttachment): string {
  if (attachment.available) return "Ready to open";
  if (["PENDING", "DOWNLOADING", "DOWNLOADED", "INSPECTING", "NEEDS_BACKFILL"].includes(attachment.state)) return "Still being captured";
  if (attachment.state === "UNSUPPORTED") return "This file type is not supported yet";
  if (attachment.state === "SKIPPED") return "File was not retained";
  return "File is unavailable";
}

function mergeConversationDirectory(
  current: readonly WhatsAppBrowserConversation[],
  incoming: readonly WhatsAppBrowserConversation[],
  sortNewest: boolean
): WhatsAppBrowserConversation[] {
  const byId = new Map<string, WhatsAppBrowserConversation>();
  for (const conversation of current) byId.set(conversation.id, conversation);
  for (const conversation of incoming) byId.set(conversation.id, conversation);
  const merged = [...byId.values()];
  if (!sortNewest) return merged;
  return merged.sort((left, right) => {
    if (!left.lastMessageAt && !right.lastMessageAt) return right.id.localeCompare(left.id);
    if (!left.lastMessageAt) return 1;
    if (!right.lastMessageAt) return -1;
    return Date.parse(right.lastMessageAt) - Date.parse(left.lastMessageAt) || right.id.localeCompare(left.id);
  });
}

function readableError(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

function mediaLabel(kind: WhatsAppBrowserAttachment["mediaKind"]): string {
  return kind.charAt(0) + kind.slice(1).toLocaleLowerCase();
}

function formatBytes(value: number | null): string {
  if (value === null) return "Ready to open";
  if (value < 1_024) return `${value} B`;
  if (value < 1_048_576) return `${Math.round(value / 1_024)} KB`;
  return `${(value / 1_048_576).toFixed(1)} MB`;
}

function shortConversationTime(value: string | null): string {
  if (!value) return "";
  const date = new Date(value);
  const now = new Date();
  if (date.toDateString() === now.toDateString()) {
    return new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" }).format(date);
  }
  return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" }).format(date);
}

function messageTime(value: string): string {
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit"
  }).format(new Date(value));
}
